import {
  DeviceRegistryCommandFunctionType,
  DeviceRegistryCommandProtocolDefinition,
  DeviceRegistryCommand
} from '@tryiris-ai/common.protojs/api/v1/device_settings';
import { generateId } from '@studio/models';
import { ViscaBridgeCommand, CommandType } from '@tryiris-ai/common.protojs/joystick/v1/joystick';

/**
 * Builds the setting object to be applied based on the VISCA command type and values.
 */
export function buildSettingFromCommand(
  cmdDef: DeviceRegistryCommand,
  command: ViscaBridgeCommand
) {
  const { type, values } = command;
  switch (type) {
    case CommandType.COMMAND_TYPE_ACTION:
      return buildAction(cmdDef, values);
    case CommandType.COMMAND_TYPE_SELECT:
      return buildSelect(cmdDef, values);
    case CommandType.COMMAND_TYPE_TOGGLE:
      return buildToggle(cmdDef, values);
    default:
      console.error('>> Command Building Error: Unsupported command type:', cmdDef.type);
      return undefined;
  }
}

function buildAction(
  cmdDef: DeviceRegistryCommand,
  values: number[]
): { action: { action: DeviceRegistryCommandFunctionType; uuid: string } } | undefined {
  let actionType: DeviceRegistryCommandFunctionType;

  if (values.length === 0) {
    actionType = DeviceRegistryCommandFunctionType.ACTION;
  } else {
    const actionValue = values[0];
    const foundDef = findDefinitionByAction(cmdDef.definitions, actionValue!);
    if (!foundDef) {
      console.error(
        `>> Action Setting Error: No definition found for action value: ${actionValue} in command ${cmdDef.key}`
      );
      return undefined;
    }

    const validActionTypes = [
      DeviceRegistryCommandFunctionType.ACTION,
      DeviceRegistryCommandFunctionType.UP,
      DeviceRegistryCommandFunctionType.DOWN,
      DeviceRegistryCommandFunctionType.RESET_SETTING
    ];
    if (!validActionTypes.includes(foundDef.type)) {
      console.error(
        `>> Action Setting Error: Unsupported action type: ${foundDef.type} for command ${cmdDef.key}`
      );
      return undefined;
    }
    actionType = foundDef.type;
  }

  return { action: { action: actionType, uuid: generateId() } };
}

function buildSelect(
  cmdDef: DeviceRegistryCommand,
  values: number[]
): { select: number } | undefined {
  if (values.length === 0) {
    console.error(`>> Select Setting Error: No value provided for SELECT command ${cmdDef.key}`);
    return undefined;
  }
  if (!cmdDef.select?.options) {
    console.error(`>> Select Setting Error: No select options defined for command: ${cmdDef.key}`);
    return undefined;
  }

  const selectValue = values[0];
  const optionIndex = cmdDef.select.options.findIndex((o) => {
    const viscaBytes = o.visca?.commandBytes;
    return (
      viscaBytes && viscaBytes.length >= 2 && viscaBytes[viscaBytes.length - 2] === selectValue
    );
  });

  if (optionIndex === -1) {
    console.error(
      `>> Select Setting Error: No option found for select value: ${selectValue} in command ${cmdDef.key}`
    );
    return undefined;
  }
  return { select: optionIndex };
}

function buildToggle(
  cmdDef: DeviceRegistryCommand,
  values: number[]
): { toggle: boolean } | undefined {
  if (values.length === 0) {
    console.error(`>> Toggle Setting Error: No value provided for TOGGLE command ${cmdDef.key}`);
    return undefined;
  }

  const toggleValue = values[0]!;
  const foundDef = findDefinitionByAction(cmdDef.definitions, toggleValue);

  if (!foundDef) {
    console.error(
      `>> Toggle Setting Error: No definition found for toggle value: ${toggleValue} in command ${cmdDef.key}`
    );
    return undefined;
  }

  const type = foundDef.type as DeviceRegistryCommandFunctionType;
  if (
    type !== DeviceRegistryCommandFunctionType.ON &&
    type !== DeviceRegistryCommandFunctionType.OFF
  ) {
    console.error(
      `>> Toggle Setting Error: Unsupported toggle type: ${type} for command ${cmdDef.key}`
    );
    return undefined;
  }
  return { toggle: type === DeviceRegistryCommandFunctionType.ON };
}

/**
 * Finds a command definition from a list by matching a VISCA byte (typically the action byte).
 * Assumes the distinguishing byte is the second to last in `commandBytes`.
 */
function findDefinitionByAction(
  definitions: DeviceRegistryCommandProtocolDefinition[] | undefined,
  action: number
): DeviceRegistryCommandProtocolDefinition | undefined {
  if (!definitions) return undefined;
  return definitions.find((d) => {
    const viscaBytes = d.visca?.commandBytes;
    return viscaBytes && viscaBytes.length >= 2 && viscaBytes[viscaBytes.length - 2] === action;
  });
}
